import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Shield,
  Key,
  Eye,
  EyeOff,
  Save,
  AlertTriangle,
  CheckCircle2,
  Info,
  Link as LinkIcon,
  ExternalLink,
  Unlink,
  CreditCard,
  Crown,
  Loader2,
  User,
  Mail,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { PrivacySettings, BYOKSettings, JiraIntegration } from "@/lib/types";
import { useSubscription, SUBSCRIPTION_TIERS } from "@/contexts/SubscriptionContext";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/contexts/AuthContext";
import { CreditTransactionHistory } from "@/components/credits/CreditTransactionHistory";
import { TopupPurchase } from "@/components/credits/TopupPurchase";
import { ActiveTopupBatches } from "@/components/credits/ActiveTopupBatches";
import { toast } from "@/hooks/use-toast";

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    tier,
    subscribed,
    subscriptionEnd,
    openCustomerPortal,
    loading: subscriptionLoading,
    usage,
    canUseReview,
    refreshCredits,
  } = useSubscription();
  const tierInfo = SUBSCRIPTION_TIERS[tier];
  const { profile, loading: profileLoading, updating, updateProfile } = useProfile();
  const { user } = useAuth();

  // Handle topup success - refresh credits
  useEffect(() => {
    const topupStatus = searchParams.get("topup");
    const creditsAdded = searchParams.get("credits");

    if (topupStatus === "success") {
      // Clear the query params
      searchParams.delete("topup");
      searchParams.delete("credits");
      setSearchParams(searchParams, { replace: true });

      // Show success toast
      toast({
        title: "Credits added!",
        description: creditsAdded
          ? `${creditsAdded} credits have been added to your account.`
          : "Your credits have been added to your account.",
      });

      // Refresh credits with a small delay for webhook processing
      const refreshAfterTopup = async () => {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await refreshCredits();
      };
      refreshAfterTopup();
    } else if (topupStatus === "cancelled") {
      searchParams.delete("topup");
      setSearchParams(searchParams, { replace: true });

      toast({
        title: "Purchase cancelled",
        description: "Your credit purchase was cancelled.",
        variant: "destructive",
      });
    }
  }, [searchParams, setSearchParams, refreshCredits]);

  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    defaultMode: "standard",
    retainMetrics: true,
    analyticsOptOut: false,
  });

  const [byokSettings, setByokSettings] = useState<BYOKSettings>({
    enabled: false,
    provider: undefined,
    apiKey: undefined,
    model: undefined,
  });

  const [jiraSettings, setJiraSettings] = useState<JiraIntegration>({
    connected: false,
    instanceUrl: undefined,
    projectKey: undefined,
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  // Profile form state
  const [fullName, setFullName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  // Initialize profile form when profile loads
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setAvatarUrl(profile.avatar_url || "");
    }
  }, [profile]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    await openCustomerPortal();
    setPortalLoading(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSaving(false);
  };

  const handleSaveProfile = async () => {
    await updateProfile({
      full_name: fullName,
      avatar_url: avatarUrl || undefined,
    });
  };

  const handleConnectJira = () => {
    setJiraSettings({
      connected: true,
      instanceUrl: "https://company.atlassian.net",
      projectKey: "PROJ",
    });
  };

  const handleDisconnectJira = () => {
    setJiraSettings({
      connected: false,
      instanceUrl: undefined,
      projectKey: undefined,
    });
  };

  const userInitials = (profile?.full_name || user?.email || "U")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="min-h-screen p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-display font-bold mb-1">Settings</h1>
        <p className="text-muted-foreground">Manage your profile, privacy, integrations, and API settings</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="glass">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="subscription" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Subscription
          </TabsTrigger>
          <TabsTrigger value="privacy" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Privacy
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <LinkIcon className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="byok" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            BYOK
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="max-w-2xl space-y-6">
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Your Profile
                </CardTitle>
                <CardDescription>Manage your personal information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {profileLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {/* Avatar */}
                    <div className="flex items-center gap-4">
                      <Avatar className="h-20 w-20">
                        <AvatarImage src={avatarUrl || undefined} alt={fullName} />
                        <AvatarFallback className="text-xl bg-primary/10 text-primary">{userInitials}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-2">
                        <Label htmlFor="avatar-url">Avatar URL</Label>
                        <Input
                          id="avatar-url"
                          placeholder="https://example.com/avatar.jpg"
                          value={avatarUrl}
                          onChange={(e) => setAvatarUrl(e.target.value)}
                          className="h-12"
                        />
                        <p className="text-xs text-muted-foreground">Enter a URL to an image to use as your avatar</p>
                      </div>
                    </div>

                    {/* Full Name */}
                    <div className="space-y-2">
                      <Label htmlFor="full-name">Full Name</Label>
                      <Input
                        id="full-name"
                        placeholder="John Doe"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="h-12"
                      />
                    </div>

                    {/* Email (read-only) */}
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{user?.email || profile?.email}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Email cannot be changed. Contact support if you need to update it.
                      </p>
                    </div>

                    {/* Save Button */}
                    <Button
                      onClick={handleSaveProfile}
                      disabled={updating}
                      className="gradient-primary text-primary-foreground"
                    >
                      {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Profile
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Account Info */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg">Account Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Account ID</span>
                    <span className="font-mono text-xs">{user?.id?.slice(0, 8)}...</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Created</span>
                    <span>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "—"}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-muted-foreground">Last Updated</span>
                    <span>{profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString() : "—"}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription">
          <div className="max-w-2xl space-y-6">
            {/* Current Plan */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Crown className="w-5 h-5 text-primary" />
                  Current Plan
                </CardTitle>
                <CardDescription>Manage your subscription and billing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-xl bg-primary/10 border-2 border-primary/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-semibold">{tierInfo.name}</h3>
                      {subscribed && <Badge className="bg-reqqy-success/20 text-reqqy-success">Active</Badge>}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {tierInfo.monthlyCredits === Infinity
                      ? "Unlimited credits per month"
                      : `${tierInfo.monthlyCredits} credits per month`}
                  </p>
                  {subscriptionEnd && (
                    <p className="text-xs text-muted-foreground">
                      Next billing date: {new Date(subscriptionEnd).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  {subscribed ? (
                    <Button onClick={handleManageSubscription} disabled={portalLoading} variant="outline">
                      {portalLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      Manage Subscription
                    </Button>
                  ) : (
                    <Button asChild className="gradient-primary text-primary-foreground">
                      <Link to="/pricing">
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade Plan
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="outline">
                    <Link to="/pricing">View All Plans</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Usage Stats */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg">Usage This Month</CardTitle>
                <CardDescription>Track your credits and usage limits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Credits Used</span>
                      <span className="text-sm text-muted-foreground">
                        {usage.loading ? (
                          <Loader2 className="w-3 h-3 animate-spin inline" />
                        ) : (
                          <>
                            {usage.creditsUsed} / {usage.creditsLimit === Infinity ? "∞" : usage.creditsLimit}
                          </>
                        )}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          !canUseReview() ? "bg-destructive" : "gradient-primary",
                        )}
                        style={{
                          width:
                            usage.creditsLimit === Infinity
                              ? "5%"
                              : `${Math.min((usage.creditsUsed / usage.creditsLimit) * 100, 100)}%`,
                        }}
                      />
                    </div>
                    {!canUseReview() && (
                      <p className="text-sm text-destructive mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        You've reached your monthly credit limit.{" "}
                        <Link to="/pricing" className="underline">
                          Upgrade your plan
                        </Link>{" "}
                        for more credits.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>


            {/* Active Top-up Batches */}
            <ActiveTopupBatches />

            {/* Credit Top-ups */}
            <TopupPurchase onPurchaseComplete={() => refreshCredits()} />

            {/* Features */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg">Plan Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {tierInfo.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Credit Transaction History */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg">Credit History</CardTitle>
                <CardDescription>Your recent credit transactions and usage</CardDescription>
              </CardHeader>
              <CardContent>
                <CreditTransactionHistory />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Privacy Tab */}
        <TabsContent value="privacy">
          <div className="max-w-2xl space-y-6">
            {/* Default Privacy Mode */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Default Privacy Mode
                </CardTitle>
                <CardDescription>
                  Choose the default privacy mode for new runs. You can override this per-run.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPrivacySettings({ ...privacySettings, defaultMode: "standard" })}
                    className={cn(
                      "p-4 rounded-xl text-left transition-all border-2",
                      privacySettings.defaultMode === "standard"
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-muted-foreground/50",
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <span className="font-medium">Standard</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Store run history for your convenience</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPrivacySettings({ ...privacySettings, defaultMode: "temporary" })}
                    className={cn(
                      "p-4 rounded-xl text-left transition-all border-2",
                      privacySettings.defaultMode === "temporary"
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-muted-foreground/50",
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-reqqy-warning" />
                      <span className="font-medium">Temporary</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Delete content after session, keep metrics only</p>
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Metrics Retention */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg">Metrics & Analytics</CardTitle>
                <CardDescription>Control what usage data is collected</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-background/50">
                  <div className="space-y-1">
                    <Label htmlFor="retain-metrics" className="font-medium">
                      Retain usage metrics
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Keep run counts and clarity scores for your dashboard
                    </p>
                  </div>
                  <Switch
                    id="retain-metrics"
                    checked={privacySettings.retainMetrics}
                    onCheckedChange={(checked) => setPrivacySettings({ ...privacySettings, retainMetrics: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-background/50">
                  <div className="space-y-1">
                    <Label htmlFor="analytics-optout" className="font-medium">
                      Opt out of analytics
                    </Label>
                    <p className="text-sm text-muted-foreground">We collect anonymous usage data to improve Reqqy</p>
                  </div>
                  <Switch
                    id="analytics-optout"
                    checked={privacySettings.analyticsOptOut}
                    onCheckedChange={(checked) => setPrivacySettings({ ...privacySettings, analyticsOptOut: checked })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Privacy Notice */}
            <Card className="glass border-primary/30 bg-primary/5">
              <CardContent className="p-4 flex items-start gap-4">
                <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-primary mb-1">Our privacy commitment</p>
                  <p className="text-muted-foreground">
                    We do not train on your data. We do not reuse your content. Enterprise customers can enable
                    zero-retention execution for complete data sovereignty.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <div className="max-w-2xl space-y-6">
            {/* Jira Integration */}
            <Card className="glass">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ExternalLink className="w-5 h-5 text-primary" />
                      Jira Integration
                    </CardTitle>
                    <CardDescription>Import and export requirements to Jira</CardDescription>
                  </div>
                  {jiraSettings.connected && (
                    <Badge className="bg-reqqy-success/20 text-reqqy-success">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {jiraSettings.connected ? (
                  <>
                    <div className="p-4 rounded-lg bg-background/50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Instance</span>
                        <span className="text-sm font-medium">{jiraSettings.instanceUrl}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Default Project</span>
                        <span className="text-sm font-medium">{jiraSettings.projectKey}</span>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                      <p className="text-sm">
                        <strong>Default behavior:</strong> Reqqy will never overwrite existing Jira content. Exported
                        stories get the <code className="bg-background/50 px-1 rounded">reqqy-reviewed</code> label.
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      onClick={handleDisconnectJira}
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      Disconnect Jira
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Connect your Jira instance to import epic/story descriptions and export Reqqy output as new
                      stories or comments.
                    </p>
                    <Button onClick={handleConnectJira} className="gradient-primary text-primary-foreground">
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Connect Jira
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* BYOK Tab */}
        <TabsContent value="byok">
          <div className="max-w-2xl space-y-6">
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Key className="w-5 h-5 text-primary" />
                  Bring Your Own Key (BYOK)
                </CardTitle>
                <CardDescription>
                  Use your own API keys for AI providers. Your data goes directly to your chosen provider.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-lg bg-background/50">
                  <div className="space-y-1">
                    <Label htmlFor="byok-enabled" className="font-medium">
                      Enable BYOK
                    </Label>
                    <p className="text-sm text-muted-foreground">Use your own API keys instead of Reqqy credits</p>
                  </div>
                  <Switch
                    id="byok-enabled"
                    checked={byokSettings.enabled}
                    onCheckedChange={(checked) => setByokSettings({ ...byokSettings, enabled: checked })}
                  />
                </div>

                {byokSettings.enabled && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="space-y-2">
                      <Label>Provider</Label>
                      <Select
                        value={byokSettings.provider}
                        onValueChange={(value) =>
                          setByokSettings({
                            ...byokSettings,
                            provider: value as BYOKSettings["provider"],
                          })
                        }
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="openai">OpenAI</SelectItem>
                          <SelectItem value="anthropic">Anthropic</SelectItem>
                          <SelectItem value="azure">Azure OpenAI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="api-key">API Key</Label>
                      <div className="relative">
                        <Input
                          id="api-key"
                          type={showApiKey ? "text" : "password"}
                          placeholder="sk-..."
                          value={byokSettings.apiKey || ""}
                          onChange={(e) => setByokSettings({ ...byokSettings, apiKey: e.target.value })}
                          className="h-12 pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <Shield className="w-3 h-3" />
                        Your API key is encrypted and stored securely
                      </p>
                    </div>

                    {byokSettings.provider && (
                      <div className="space-y-2">
                        <Label>Model</Label>
                        <Select
                          value={byokSettings.model}
                          onValueChange={(value) => setByokSettings({ ...byokSettings, model: value })}
                        >
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                            {byokSettings.provider === "openai" && (
                              <>
                                <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                                <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                                <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
                              </>
                            )}
                            {byokSettings.provider === "anthropic" && (
                              <>
                                <SelectItem value="claude-3-opus">Claude 3 Opus</SelectItem>
                                <SelectItem value="claude-3-sonnet">Claude 3 Sonnet</SelectItem>
                                <SelectItem value="claude-3-haiku">Claude 3 Haiku</SelectItem>
                              </>
                            )}
                            {byokSettings.provider === "azure" && (
                              <>
                                <SelectItem value="gpt-4">GPT-4</SelectItem>
                                <SelectItem value="gpt-35-turbo">GPT-3.5 Turbo</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="p-4 rounded-lg bg-primary/10 border border-primary/30 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-primary mb-1">Maximum privacy with BYOK</p>
                        <p className="text-muted-foreground">
                          When BYOK is enabled, your data goes directly to your chosen provider. Reqqy never sees or
                          stores your content.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Plan Notice */}
            <Card className="glass border-reqqy-warning/30 bg-reqqy-warning/5">
              <CardContent className="p-4 flex items-center gap-4">
                <AlertTriangle className="w-6 h-6 text-reqqy-warning flex-shrink-0" />
                <div>
                  <p className="font-medium">Business or Enterprise plan required</p>
                  <p className="text-sm text-muted-foreground">
                    BYOK is available on Business and Enterprise plans.{" "}
                    <Link to="/pricing" className="text-primary hover:underline">
                      Upgrade your plan
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="max-w-2xl mt-8 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="gradient-primary text-primary-foreground hover:opacity-90"
        >
          {isSaving ? (
            <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
